import type { ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { useLocalStorageSync } from "./hooks/useLocalStorageSync";
import { initTheme } from "./hooks/useTheme";
import Layout from "./components/Layout";
import DashboardPage from "./pages/DashboardPage";
import SettingsPage from "./pages/SettingsPage";
import EvaluatePage from "./pages/EvaluatePage";
import EmployeeDetailPage from "./pages/EmployeeDetailPage";
import EvaluationMatrixPage from "./pages/EvaluationMatrixPage";
import AuthPage from "./pages/AuthPage";
import NotFound from "./pages/NotFound";
import EmployeeListPage from "./pages/EmployeeListPage";
import OrgChartPage from "./pages/OrgChartPage";
import ApplicantManagementPage from "./pages/ApplicantManagementPage";
import ApplicantDetailPage from "./pages/ApplicantDetailPage";
import ApplyPage from "./pages/ApplyPage";
import CostOverviewPage from "./pages/CostOverviewPage";
import HelpPage from "./pages/HelpPage";
import EmployeeDirectoryPage from "./pages/EmployeeDirectoryPage";
import WorkProcessesPage from "./pages/WorkProcessesPage";
import VehicleUploadsOverviewPage from "./pages/VehicleUploadsOverviewPage";
import DocumentPreviewPage from "./pages/DocumentPreviewPage";
import SurveyPage from "./pages/SurveyPage";
import InterviewResponsePage from "./pages/InterviewResponsePage";
import SafetyRunPage from "./pages/SafetyRunPage";

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Wird geladen...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Protected route without Layout wrapper (for fullscreen pages like document preview)
const ProtectedRouteNoLayout = ({ children }: { children: ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Wird geladen...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

const queryClient = new QueryClient();

initTheme();

const App = () => {
  useLocalStorageSync();
  
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <main className="min-h-screen">
              <Routes>
                <Route path="/auth" element={<AuthPage />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/employees"
                  element={
                    <ProtectedRoute>
                      <EmployeeListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orgchart"
                  element={
                    <ProtectedRoute>
                      <OrgChartPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/matrix"
                  element={
                    <ProtectedRoute>
                      <EvaluationMatrixPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/evaluate/:employeeId"
                  element={
                    <ProtectedRoute>
                      <EvaluatePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/employee/:employeeId"
                  element={
                    <ProtectedRoute>
                      <EmployeeDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/applicants"
                  element={
                    <ProtectedRoute>
                      <ApplicantManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/applicants/:id"
                  element={
                    <ProtectedRoute>
                      <ApplicantDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/apply" element={<ApplyPage />} />
                <Route path="/interview-response/:token" element={<InterviewResponsePage />} />
                <Route path="/safety/run/:token" element={<SafetyRunPage />} />
                <Route
                  path="/cost-overview"
                  element={
                    <ProtectedRoute>
                      <CostOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/directory"
                  element={
                    <ProtectedRoute>
                      <EmployeeDirectoryPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/work-processes"
                  element={
                    <ProtectedRoute>
                      <WorkProcessesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vehicle-uploads"
                  element={
                    <ProtectedRoute>
                      <VehicleUploadsOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/help"
                  element={
                    <ProtectedRoute>
                      <HelpPage />
                    </ProtectedRoute>
                  }
                />
                {/* Survey - for all authenticated users */}
                <Route
                  path="/survey"
                  element={
                    <ProtectedRoute>
                      <SurveyPage />
                    </ProtectedRoute>
                  }
                />
                {/* Document Preview - fullscreen without sidebar/navigation */}
                <Route
                  path="/preview"
                  element={
                    <ProtectedRouteNoLayout>
                      <DocumentPreviewPage />
                    </ProtectedRouteNoLayout>
                  }
                />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
